import {
  IScoreSettingsRulePatch,
  PatchScoringSettings,
} from 'crono-fe-common/types/DTO/patchScoringSettings';
import {
  ExternalProperty,
  ExternalPropertyValueType,
  TableType,
} from 'crono-fe-common/types/externalProperty';
import useGetExternalProperty from 'hooks/services/externalProperty/useGetExternalProperty';
import { useCallback, useMemo } from 'react';
import { createHashedObject } from 'utils/uiHelpers';
import { useGetStatusesOptions } from 'utils/businessLogic';

export type LeadScorePropertySource = 'default' | 'external';

export type LeadScoreProperty = {
  propertyKey: string;
  publicName: string;
  valueType: ExternalPropertyValueType;
  tableType: TableType;
  source: LeadScorePropertySource;
  isAiVariable?: boolean;
  externalPropertyId?: number;
  accountProperty?: IScoreSettingsRulePatch['accountProperty'];
  prospectProperty?: IScoreSettingsRulePatch['prospectProperty'];
  options?: string[];
  labels?: string[];
  aiVariableSettings?: ExternalProperty['aiVariableSettings'];
};

type LeadScorePropertiesByType = Partial<
  Record<PatchScoringSettings['type'], LeadScoreProperty[]>
>;

const makeDefaultAccountProperties = (
  statuses: { option: string; label: string }[],
): LeadScoreProperty[] => [
  {
    propertyKey: 'account-ActualStatus',
    publicName: 'Status',
    valueType: 'List',
    tableType: 'Account',
    source: 'default',
    accountProperty: 'ActualStatus',
    options: statuses.map((s) => s.option),
    labels: statuses.map((s) => s.label),
  },
  {
    propertyKey: 'account-Industry',
    publicName: 'Industry',
    valueType: 'Text',
    tableType: 'Account',
    source: 'default',
    accountProperty: 'Industry',
  },
  {
    propertyKey: 'account-Country',
    publicName: 'Country',
    valueType: 'Text',
    tableType: 'Account',
    source: 'default',
    accountProperty: 'Country',
  },
  {
    propertyKey: 'account-NumberOfEmployees',
    publicName: 'Size',
    valueType: 'Numeric',
    tableType: 'Account',
    source: 'default',
    accountProperty: 'NumberOfEmployees',
  },
  {
    propertyKey: 'account-AnnualRevenue',
    publicName: 'Revenue',
    valueType: 'Numeric',
    tableType: 'Account',
    source: 'default',
    accountProperty: 'AnnualRevenue',
  },
  {
    propertyKey: 'account-CreatedDate',
    publicName: 'CreatedDate',
    valueType: 'Date',
    tableType: 'Account',
    source: 'default',
    accountProperty: 'CreatedDate',
  },
  {
    propertyKey: 'account-LastActivityDate',
    publicName: 'Last Activity',
    valueType: 'Date',
    tableType: 'Account',
    source: 'default',
    accountProperty: 'LastActivityDate',
  },
];

const makeDefaultProspectProperties = (
  statuses: { option: string; label: string }[],
): LeadScoreProperty[] => [
  {
    propertyKey: 'prospect-ActualStatus',
    publicName: 'Status',
    valueType: 'List',
    tableType: 'Prospect',
    source: 'default',
    prospectProperty: 'ActualStatus',
    options: statuses.map((s) => s.option),
    labels: statuses.map((s) => s.label),
  },
  {
    propertyKey: 'prospect-Location',
    publicName: 'Location',
    valueType: 'Text',
    tableType: 'Prospect',
    source: 'default',
    prospectProperty: 'Location',
  },
  {
    propertyKey: 'prospect-Title',
    publicName: 'Job Title',
    valueType: 'Text',
    tableType: 'Prospect',
    source: 'default',
    prospectProperty: 'Title',
  },
  {
    propertyKey: 'prospect-CreatedDate',
    publicName: 'CreatedDate',
    valueType: 'Date',
    tableType: 'Prospect',
    source: 'default',
    prospectProperty: 'CreatedDate',
  },
  {
    propertyKey: 'prospect-LastActivityDate',
    publicName: 'Last Activity',
    valueType: 'Date',
    tableType: 'Prospect',
    source: 'default',
    prospectProperty: 'LastActivityDate',
  },
];

const buildDefaultProperties = (
  accountStatuses: { option: string; label: string }[],
  prospectStatuses: { option: string; label: string }[],
) => ({
  Account: makeDefaultAccountProperties(accountStatuses),
  Prospect: makeDefaultProspectProperties(prospectStatuses),
});

const toLeadScoreProperty = (
  externalProperty: ExternalProperty,
): LeadScoreProperty => ({
  propertyKey: `external-${externalProperty.id}`,
  publicName: externalProperty.publicName ?? '',
  valueType: externalProperty.valueType,
  tableType: externalProperty.tableType,
  source: 'external',
  isAiVariable: Boolean(externalProperty.aiVariableSettings),
  externalPropertyId: externalProperty.id,
  options: externalProperty.options ?? undefined,
  labels: externalProperty.labels ?? undefined,
  aiVariableSettings: externalProperty.aiVariableSettings,
});

const getPropertyKeyForRule = (rule: Partial<IScoreSettingsRulePatch>) => {
  if (
    rule.externalPropertyId !== undefined &&
    rule.externalPropertyId !== null
  ) {
    return `external-${rule.externalPropertyId}`;
  }

  if (rule.accountProperty) {
    return `account-${rule.accountProperty}`;
  }

  if (rule.prospectProperty) {
    return `prospect-${rule.prospectProperty}`;
  }

  return undefined;
};

const normalizeType = (
  type: PatchScoringSettings['type'],
  tab: PatchScoringSettings['type'],
) => {
  if (tab === 'Lead' && type === 'Lead') return 'Prospect';
  if (tab === 'Account' && type === 'Prospect') return 'Account';
  return type;
};

export const useLeadScoreExternalProps = (
  tab: PatchScoringSettings['type'],
) => {
  const { statuses: prospectStatuses } = useGetStatusesOptions('prospect');
  const { statuses: accountStatuses } = useGetStatusesOptions('account');

  const { data: exPropContacts } = useGetExternalProperty({
    tableType: tab === 'Lead' ? 'Lead' : 'Prospect',
    onlyInsert: false,
  });

  const { data: aiVariableContacts } = useGetExternalProperty({
    tableType: 'Prospect',
    onlyAiVariables: true,
  });

  const { data: exPropCompanies } = useGetExternalProperty({
    tableType: 'Account',
    onlyInsert: false,
    withLead: true,
  });

  const { data: aiVariableCompanies } = useGetExternalProperty({
    tableType: 'Account',
    onlyTag: false,
    onlyAiVariables: true,
  });

  const defaultPropertiesByType = useMemo<LeadScorePropertiesByType>(() => {
    return buildDefaultProperties(
      accountStatuses ?? [],
      prospectStatuses ?? [],
    );
  }, [accountStatuses, prospectStatuses]);

  const externalProps = useMemo(() => {
    return [
      ...(exPropContacts?.data?.data ?? []),
      ...(aiVariableContacts?.data?.data ?? []),
      ...(exPropCompanies?.data?.data ?? []),
      ...(aiVariableCompanies?.data?.data ?? []),
    ];
  }, [
    exPropContacts,
    aiVariableContacts,
    exPropCompanies,
    aiVariableCompanies,
  ]);

  const dedupedExternalProps = useMemo(() => {
    const seen = new Set<number>();

    return externalProps.filter((prop) => {
      if (seen.has(prop.id)) return false;
      seen.add(prop.id);
      return true;
    });
  }, [externalProps]);

  const externalProperties = useMemo(
    () => dedupedExternalProps.map(toLeadScoreProperty),
    [dedupedExternalProps],
  );

  const externalPropertiesByType = useMemo<LeadScorePropertiesByType>(() => {
    return createHashedObject(externalProperties, 'tableType', true);
  }, [externalProperties]);

  const allProperties = useMemo<LeadScoreProperty[]>(() => {
    return [
      ...(defaultPropertiesByType.Account ?? []),
      ...(defaultPropertiesByType.Prospect ?? []),
      ...(defaultPropertiesByType.Lead ?? []),
      ...externalProperties,
    ];
  }, [defaultPropertiesByType, externalProperties]);

  const propertyMap = useMemo(
    () =>
      allProperties.reduce<Record<string, LeadScoreProperty>>(
        (acc, property) => {
          acc[property.propertyKey] = property;
          return acc;
        },
        {},
      ),
    [allProperties],
  );

  const propertiesByType = useMemo<LeadScorePropertiesByType>(() => {
    const hashed = allProperties.reduce<LeadScorePropertiesByType>(
      (acc, property) => {
        const type = property.tableType as PatchScoringSettings['type'];
        acc[type] = [...(acc[type] ?? []), property];
        return acc;
      },
      {},
    );

    if (tab === 'Lead') {
      hashed.Lead = [...(hashed.Prospect ?? []), ...(hashed.Lead ?? [])];
      delete hashed.Prospect;
    }

    if (tab === 'Account') {
      delete hashed.Lead;
    }

    if (tab === 'Account') {
      delete hashed.Prospect;
      delete hashed.Lead;
    }

    return hashed;
  }, [allProperties, tab]);

  const getRulesHashedByType = useCallback(
    (rules: Partial<IScoreSettingsRulePatch>[]) => {
      const hashedRules = rules.reduce<
        Partial<
          Record<
            PatchScoringSettings['type'],
            Partial<IScoreSettingsRulePatch>[]
          >
        >
      >((acc, rule) => {
        if (rule.delete) return acc;

        const propertyKey = getPropertyKeyForRule(rule);
        if (!propertyKey) return acc;

        const property = propertyMap[propertyKey];
        if (!property) return acc;

        const type = normalizeType(
          property.tableType as PatchScoringSettings['type'],
          tab,
        );

        acc[type] = [...(acc[type] ?? []), rule];
        return acc;
      }, {});

      if (tab === 'Lead') {
        hashedRules.Lead = [
          ...(hashedRules.Prospect ?? []),
          ...(hashedRules.Lead ?? []),
        ];
        delete hashedRules.Prospect;
      }

      if (tab === 'Prospect') {
        delete hashedRules.Lead;
      }

      if (tab === 'Account') {
        delete hashedRules.Prospect;
        delete hashedRules.Lead;
      }

      return hashedRules;
    },
    [propertyMap, tab],
  );

  const getRulesHashedByTypeAndProperty = useCallback(
    (rules: Partial<IScoreSettingsRulePatch>[]) => {
      const hashedRules = rules.reduce<
        Partial<
          Record<
            PatchScoringSettings['type'],
            Record<string, Partial<IScoreSettingsRulePatch>[]>
          >
        >
      >(
        (acc, rule) => {
          if (rule.delete) return acc;

          const propertyKey = getPropertyKeyForRule(rule);
          if (!propertyKey) return acc;

          const property = propertyMap[propertyKey];
          if (!property) return acc;

          const type = normalizeType(
            property.tableType as PatchScoringSettings['type'],
            tab,
          );

          acc[type] = {
            ...(acc[type] ?? {}),
            [propertyKey]: [...(acc[type]?.[propertyKey] ?? []), rule],
          };

          return acc;
        },
        {
          Prospect: {},
          Lead: {},
          Account: {},
        },
      );

      if (tab === 'Lead' && hashedRules.Lead) {
        const prospectRules = hashedRules.Prospect ?? {};
        const leadRules = hashedRules.Lead ?? {};
        const mergedKeys = new Set([
          ...Object.keys(prospectRules),
          ...Object.keys(leadRules),
        ]);
        hashedRules.Prospect = Object.fromEntries(
          [...mergedKeys].map((key) => [
            key,
            [...(prospectRules[key] ?? []), ...(leadRules[key] ?? [])],
          ]),
        );

        hashedRules.Lead = { ...hashedRules.Prospect };

        delete hashedRules.Prospect;
      }

      if (tab === 'Account') {
        delete hashedRules.Prospect;
      }

      if (tab === 'Prospect') {
        delete hashedRules.Lead;
      }

      return hashedRules;
    },
    [propertyMap, tab],
  );

  return {
    propertyMap,
    propertiesByType,
    defaultPropertiesByType,
    externalPropertiesByType,
    getRulesHashedByType,
    getRulesHashedByTypeAndProperty,
    getPropertyKeyForRule,
  };
};
